import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {
    FileFormPropsCallback,
    XLSXError,
    XlsxMultipleFilesForm,
} from 'components/common/XlsxMultipleFilesForm';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';
import {DialogTitle} from 'components/ui-kit/Modal/styled';
import {Spinner} from 'components/ui-kit/Spinner';
import {tableStateChanged} from 'models/registries/gtinsGrid/table';
import {
    isProductCardLoadStatusPolling$,
    isModalOpen$,
    isModalOpenChanged,
    loadProductCardFileFx,
    loadProductCardFileStatus$,
} from 'models/registries/gtinsGrid/upload';

import {ButtonsContainer, InternalProductHint, ModalTitleContent, SpinnerLayer} from './styled';

export const GtinsUploadList = () => {
    const {t} = useTranslation();
    const isModalOpen = useStore(isModalOpen$);
    const isLoadProductCardFilePending = useStore(loadProductCardFileFx.pending);
    const isProductCardLoadStatusPolling = useStore(isProductCardLoadStatusPolling$);
    const loadProductCardFileStatus = useStore(loadProductCardFileStatus$);
    const [file, setFile] = useState<File | null>(null);
    const [errorsList, setErrorsList] = useState<XLSXError[]>([]);
    const loading = isLoadProductCardFilePending || isProductCardLoadStatusPolling;

    useEffect(() => {
        if (!loadProductCardFileStatus) return;

        if (loadProductCardFileStatus.status === 'SUCCESS') {
            setErrorsList([]);
            setFile(null);
            isModalOpenChanged(false);
            tableStateChanged([]);
            toast.success(t('pages.registries.uploadSuccess'));
            return;
        }

        if (loadProductCardFileStatus.status === 'FAILED') {
            const mappedErrors = (loadProductCardFileStatus.errors || []).map((item) => ({
                field: item.fieldName || `row ${item.row}`,
                error: item.message,
            }));

            setErrorsList(
                mappedErrors.length
                    ? mappedErrors
                    : [
                          {
                              field: 'file',
                              error: loadProductCardFileStatus.error || t('errors.occurred'),
                          },
                      ],
            );
        }
    }, [loadProductCardFileStatus, t]);

    const handleClosed = () => {
        isModalOpenChanged(false);
        setErrorsList([]);
        setFile(null);
    };

    const handleFileChanged = ({file}: FileFormPropsCallback) => {
        setFile(file ?? null);
        setErrorsList([]);
    };

    const onSubmit = () => {
        if (!file || loading) return;
        setErrorsList([]);
        loadProductCardFileFx(file);
    };

    const ModalFooter = (
        <ButtonsContainer>
            <Button variant={ButtonVariant.Outline} onClick={handleClosed}>
                {t('components.modal.Close')}
            </Button>
            <Button disabled={!file || loading} onClick={onSubmit}>
                {t('components.form.Submit')}
            </Button>
        </ButtonsContainer>
    );

    const ModalTitle = (
        <ModalTitleContent>
            <DialogTitle size={4}>{t('pages.registries.uploadGtinTitle')}</DialogTitle>
            <InternalProductHint>
                {t('pages.registries.uploadGtinInternalProductHint')}
            </InternalProductHint>
        </ModalTitleContent>
    );

    return (
        <Modal
            show={isModalOpen}
            onClose={handleClosed}
            title={ModalTitle}
            footer={ModalFooter}
            modalWidth={0}
        >
            <XlsxMultipleFilesForm
                onChange={handleFileChanged}
                errors={errorsList}
                serverValidate
            />
            {loading && (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}
        </Modal>
    );
};
